import { useState, useEffect } from 'react';
import { getCategories, API_BASE } from '../api';
import { styles } from '../styles';
import { BackIcon, Spinner } from '../components/Icons';

const PRESETS = [
  { key: '6m', label: '6 Months', params: { months: 6 } },
  { key: '12m', label: '12 Months', params: { months: 12 } },
  { key: 'year', label: 'Year', params: {} },
];

const CATEGORY_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
  '#a855f7', '#d946ef', '#64748b', '#78716c', '#84cc16',
];

function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function getDateRange(preset, selectedYear) {
  const now = new Date();
  let startDate, endDate;
  
  if (preset === 'year') {
    startDate = new Date(selectedYear, 0, 1);
    endDate = new Date(selectedYear, 11, 31, 23, 59, 59);
  } else {
    const months = PRESETS.find(p => p.key === preset)?.params.months || 6;
    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    startDate = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
  }
  
  return { startDate, endDate };
}

export default function HistoryScreen({ onBack }) {
  const [preset, setPreset] = useState('6m');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [categoryId, setCategoryId] = useState('');
  const [userId, setUserId] = useState('');
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    
    const params = new URLSearchParams();
    if (preset === 'year') {
      params.set('year', selectedYear);
    } else {
      const p = PRESETS.find(p => p.key === preset);
      if (p?.params.months) params.set('months', p.params.months);
    }
    if (categoryId) params.set('category_id', categoryId);
    if (userId) params.set('user_id', userId);

    fetch(`${API_BASE}/history?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [preset, selectedYear, categoryId, userId]);

  const handleExport = () => {
    const { startDate, endDate } = getDateRange(preset, selectedYear);
    
    const params = new URLSearchParams();
    params.set('start_date', startDate.toISOString());
    params.set('end_date', endDate.toISOString());
    if (categoryId) params.set('category_id', categoryId);
    if (userId) params.set('user_id', userId);
    
    window.open(`${API_BASE}/receipts/export?${params}`, '_blank');
  };

  const maxValue = data ? Math.max(...data.months.map(m => Math.max(m.total, m.budget))) : 0;
  const isStacked = !categoryId && data?.categories?.length > 0;
  
  const categoryColorMap = {};
  const categoryBudgetMap = {};
  if (data?.categories) {
    data.categories.forEach((cat, i) => {
      categoryColorMap[cat.id] = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
    });
  }
  if (data?.category_budgets) {
    Object.entries(data.category_budgets).forEach(([id, budget]) => {
      categoryBudgetMap[id] = budget;
    });
  }

  const handleSegmentClick = (e, cat, spent, monthLabel) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      catId: cat.id,
      catName: cat.name,
      spent,
      budget: categoryBudgetMap[cat.id] || 0,
      month: monthLabel,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const closeTooltip = () => setTooltip(null);

  return (
    <div style={styles.screen} onClick={closeTooltip}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>History</h1>
        <button style={histStyles.exportBtn} onClick={handleExport} title="Export CSV">
          <DownloadIcon />
        </button>
      </header>

      {/* Preset Selector */}
      <div style={histStyles.presets}>
        {PRESETS.map(p => (
          <button
            key={p.key}
            style={{
              ...histStyles.presetBtn,
              ...(preset === p.key ? histStyles.presetActive : {}),
            }}
            onClick={() => setPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Year picker when "Year" preset is selected */}
      {preset === 'year' && (
        <div style={histStyles.yearPicker}>
          {years.map(y => (
            <button
              key={y}
              style={{
                ...histStyles.yearBtn,
                ...(selectedYear === y ? histStyles.yearActive : {}),
              }}
              onClick={() => setSelectedYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={histStyles.filters}>
        <select
          style={histStyles.filterSelect}
          value={categoryId}
          onChange={e => setCategoryId(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.filter(c => c.is_active).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          style={histStyles.filterSelect}
          value={userId}
          onChange={e => setUserId(e.target.value)}
        >
          <option value="">All Users</option>
          <option value="00000000-0000-0000-0000-000000000001">LCG</option>
          <option value="00000000-0000-0000-0000-000000000003">SGW</option>
        </select>
      </div>

      {loading ? (
        <div style={styles.centered}><Spinner /></div>
      ) : !data ? (
        <p style={histStyles.empty}>Could not load history</p>
      ) : (
        <>
          {/* Summary */}
          <div style={histStyles.summary}>
            <div style={histStyles.summaryItem}>
              <span style={histStyles.summaryValue}>
                ${data.summary.total_spent.toLocaleString()}
              </span>
              <span style={histStyles.summaryLabel}>Total Spent</span>
            </div>
            <div style={histStyles.summaryItem}>
              <span style={histStyles.summaryValue}>
                ${data.summary.average_monthly.toLocaleString()}
              </span>
              <span style={histStyles.summaryLabel}>Monthly Avg</span>
            </div>
            <div style={histStyles.summaryItem}>
              <span style={{
                ...histStyles.summaryValue,
                color: data.summary.total_spent <= data.summary.total_budget ? '#10b981' : '#ef4444'
              }}>
                {data.summary.total_budget > 0 
                  ? `${Math.round((data.summary.total_spent / data.summary.total_budget) * 100)}%`
                  : '—'}
              </span>
              <span style={histStyles.summaryLabel}>Of Budget</span>
            </div>
          </div>

          {/* Chart */}
          <div style={histStyles.chart}>
            {data.months.map((m, i) => (
              <div key={i} style={histStyles.barGroup}>
                <div style={histStyles.barContainer}>
                  {m.budget > 0 && (
                    <div
                      style={{
                        ...histStyles.budgetBar,
                        height: `${(m.budget / maxValue) * 100}%`,
                      }}
                    />
                  )}
                  {isStacked && m.by_category ? (
                    <div style={{
                      ...histStyles.stackedBar,
                      height: `${(m.total / maxValue) * 100}%`,
                    }}>
                      {data.categories.map(cat => {
                        const catTotal = m.by_category[cat.id] || 0;
                        if (catTotal === 0) return null;
                        const pct = (catTotal / m.total) * 100;
                        return (
                          <div
                            key={cat.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSegmentClick(e, cat, catTotal, m.label);
                            }}
                            style={{
                              width: '100%',
                              height: `${pct}%`,
                              backgroundColor: categoryColorMap[cat.id],
                              minHeight: pct > 0 ? 1 : 0,
                              cursor: 'pointer',
                            }}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      style={{
                        ...histStyles.spendBar,
                        height: `${(m.total / maxValue) * 100}%`,
                        backgroundColor: m.total > m.budget && m.budget > 0 ? '#ef4444' : '#6366f1',
                      }}
                    />
                  )}
                </div>
                <span style={histStyles.barLabel}>
                  {m.label.split(' ')[0]}
                </span>
                <span style={histStyles.barAmount}>
                  ${m.total >= 1000 ? `${(m.total/1000).toFixed(1)}k` : Math.round(m.total)}
                </span>
              </div>
            ))}
          </div>

          {/* Tooltip */}
          {tooltip && (
            <div style={histStyles.tooltip} onClick={e => e.stopPropagation()}>
              <div style={histStyles.tooltipHeader}>{tooltip.catName}</div>
              <div style={histStyles.tooltipMonth}>{tooltip.month}</div>
              <div style={histStyles.tooltipRow}>
                <span>Spent</span>
                <span style={histStyles.tooltipValue}>${tooltip.spent.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              {tooltip.budget > 0 && (
                <>
                  <div style={histStyles.tooltipRow}>
                    <span>Budget</span>
                    <span style={histStyles.tooltipValue}>${tooltip.budget.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div style={{
                    ...histStyles.tooltipRow,
                    color: tooltip.spent <= tooltip.budget ? '#10b981' : '#ef4444',
                    fontWeight: 600,
                  }}>
                    <span>{tooltip.spent <= tooltip.budget ? 'Under' : 'Over'}</span>
                    <span>${Math.abs(tooltip.budget - tooltip.spent).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Legend */}
          <div style={histStyles.legend}>
            {isStacked ? (
              data.categories.slice(0, 6).map(cat => (
                <div key={cat.id} style={histStyles.legendItem}>
                  <div style={{...histStyles.legendDot, backgroundColor: categoryColorMap[cat.id]}} />
                  <span>{cat.name}</span>
                </div>
              ))
            ) : (
              <>
                <div style={histStyles.legendItem}>
                  <div style={{...histStyles.legendDot, backgroundColor: '#6366f1'}} />
                  <span>Spent</span>
                </div>
                <div style={histStyles.legendItem}>
                  <div style={{...histStyles.legendDot, backgroundColor: '#e2e8f0'}} />
                  <span>Budget</span>
                </div>
                <div style={histStyles.legendItem}>
                  <div style={{...histStyles.legendDot, backgroundColor: '#ef4444'}} />
                  <span>Over Budget</span>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const histStyles = {
  exportBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  presets: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  presetBtn: {
    flex: 1,
    padding: '10px 12px',
    fontSize: 14,
    fontWeight: 500,
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    cursor: 'pointer',
  },
  presetActive: {
    backgroundColor: '#6366f1',
    color: 'white',
    borderColor: '#6366f1',
  },
  yearPicker: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    overflowX: 'auto',
  },
  yearBtn: {
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 500,
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  yearActive: {
    backgroundColor: '#f1f5f9',
    color: '#1e293b',
    borderColor: '#94a3b8',
  },
  filters: {
    display: 'flex',
    gap: 10,
    marginBottom: 20,
  },
  filterSelect: {
    flex: 1,
    padding: '10px 12px',
    fontSize: 14,
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    color: '#1e293b',
  },
  summary: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
  },
  summaryItem: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 14,
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1e293b',
  },
  summaryLabel: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 2,
  },
  chart: {
    display: 'flex',
    gap: 4,
    height: 180,
    alignItems: 'flex-end',
    marginBottom: 16,
    paddingBottom: 40,
    position: 'relative',
  },
  barGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
  },
  barContainer: {
    flex: 1,
    width: '100%',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  budgetBar: {
    position: 'absolute',
    bottom: 0,
    width: '80%',
    backgroundColor: '#e2e8f0',
    borderRadius: 4,
  },
  spendBar: {
    width: '60%',
    borderRadius: 4,
    position: 'relative',
    zIndex: 1,
    minHeight: 2,
  },
  stackedBar: {
    width: '60%',
    borderRadius: 4,
    position: 'relative',
    zIndex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column-reverse',
  },
  barLabel: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 6,
  },
  barAmount: {
    fontSize: 9,
    color: '#94a3b8',
    marginTop: 2,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    marginTop: 8,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#64748b',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 3,
  },
  empty: {
    textAlign: 'center',
    color: '#94a3b8',
    padding: 40,
  },
  tooltip: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    border: '1px solid #e2e8f0',
    minWidth: 180,
    zIndex: 100,
  },
  tooltipHeader: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: 2,
  },
  tooltipMonth: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 12,
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 14,
    color: '#475569',
    marginBottom: 6,
  },
  tooltipValue: {
    fontWeight: 500,
  },
};